import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { TriggerConfigurationService } from '@services/administration/machine/trigger/trigger-configuration.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { NotificationService } from '@services/notification/notification.service';
import { UtilsService } from '@services/utils/utils.service';

import { TriggerConfiguration } from '@models/adminstration/machine/trigger/trigger-configuration';
import { Machine } from '@models/machine/machine';

import {
    TriggerConfigurationEditComponent
} from '@components/administration/machine/machine-configuration/trigger/trigger-configuration/modals/trigger-configuration-edit/trigger-configuration-edit.component';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { ModalService } from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-trigger-configuration-action',
    templateUrl: './trigger-configuration-action.component.html',
    styleUrls: ['./trigger-configuration-action.component.scss'],
    standalone: false
})
export class TriggerConfigurationActionComponent implements OnInit, OnChanges {

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  machine: Machine = new Machine();

  @Input()
  triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Input()
  isGapDetectionInProgress: any = {};

  @Input()
  isTriggerActivatable: boolean | undefined = undefined;

  @Output()
  deleteEvent: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  @Output()
  updatedTriggerConfigurationEvent: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  deleteButtonIsDisabled: boolean = true;
  activateButtonIsDisabled: boolean = true;
  deactivateButtonIsDisabled: boolean = true;
  forceTriggerIsDisabled: boolean = true;
  showActivateTriggerButton: boolean = false;
  showDeactivateTriggerButton: boolean = false;

  private deleteMessageTitle = '';
  private deleteMessageBody = '';
  private deleteMessageOK = '';
  private activateMessageTitle = '';
  private activateMessageBody = '';
  private activateMessageOK = '';
  private deactivateMessageTitle = '';
  private deactivateMessageBody = '';
  private deactivateMessageOK = '';
  private forceTriggerExecutionMessageTitle = '';
  private forceTriggerExecutionMessageBody = '';
  private forceTriggerExecutionMessageOK = '';
  private GAP_DETECTION_IN_PROGRESS_STATUS_ERROR = 911;
  private COMPONENT_NAME: string = 'Trigger Configuration Action';

  constructor(
      private translate: TranslateService,
      private confirmationDialogService: ConfirmationDialogService,
      public loadingIndicator: LoadingIndicatorService,
      public notification: NotificationService,
      private modalService: ModalService,
      private triggerConfigurationService: TriggerConfigurationService,
      private utilsService: UtilsService) {
    this.deleteMessageTitle = this.translate.instant('Delete trigger configuration');
    this.deleteMessageBody = this.translate.instant('Do you really want to permanently delete the trigger configuration %s ?');
    this.deleteMessageOK = this.translate.instant('Delete');

    this.activateMessageTitle = this.translate.instant('Activate trigger');
    this.activateMessageBody = this.translate.instant('Do you really want to activate trigger %s ?');
    this.activateMessageOK = this.translate.instant('Activate');

    this.deactivateMessageTitle = this.translate.instant('Deactivate trigger');
    this.deactivateMessageBody = this.translate.instant('Do you really want to deactivate trigger %s?');
    this.deactivateMessageOK = this.translate.instant('Deactivate');

    this.forceTriggerExecutionMessageTitle = this.translate.instant('Force trigger execution');
    this.forceTriggerExecutionMessageBody = this.translate.instant('Do you really want to force the execution of the trigger %s ?');
    this.forceTriggerExecutionMessageOK = this.translate.instant('Execute');
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.initialize();
  }

  setDeleteButtonIsDisabled(): void {
    this.deleteButtonIsDisabled = this.triggerConfigurationService.isDeleteTriggerConfigurationButtonDisabled(this.userRole.isAdminRole,
        this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole, this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  setActivateButtonIsDisabled(): void {
    this.activateButtonIsDisabled = this.triggerConfigurationService.isActivateTriggerConfigurationButtonDisabled(this.userRole.isAdminRole,
        this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole, this.isTriggerActivatable);
  }

  setDeactivateButtonIsDisabled(): void {
    this.deactivateButtonIsDisabled = !this.utilsService.isAdminOrHigher(this.userRole.isAdminRole, this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole);
  }

  setForceTriggerIsDisabled(): void {
    this.forceTriggerIsDisabled = this.triggerConfigurationService.isActivateTriggerConfigurationButtonDisabled(this.userRole.isAdminRole, this.userRole.isTechAdminRole,
        this.userRole.isSuperAdminRole, this.isTriggerActivatable);
  }

  setShowDeactivateTriggerButton(): void {
    this.showDeactivateTriggerButton = this.triggerConfigurationService.showDeactivateTriggerButton(this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  setShowActivateTriggerButton(): void {
    this.showActivateTriggerButton = this.triggerConfigurationService.showActivateTriggerButton(this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  onDeleteTriggerConfiguration(): void {
    const deleteMessageBody = this.deleteMessageBody.replace('%s', this.triggerConfiguration.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.deleteMessageTitle, deleteMessageBody, '', this.deleteMessageOK)
    .subscribe(
        {
          next: userConfirmed => {
            if (userConfirmed) {
              this.onUserConfirmedDeleteTriggerConfiguration();
            }
          }, error: () => {
            this.loadingIndicator.hide();
          }
        });
  }

  onEditTriggerConfiguration(): void {
    const modalRef = this.getModal();

    modalRef.result.then((triggerConfiguration: TriggerConfiguration) => {
      this.updatedTriggerConfigurationEvent.emit(triggerConfiguration);
    }).catch(err => {
      console.log(err);
    });
  }

  onActivateTriggerConfig(): void {
    const activateMessageBody = this.activateMessageBody.replace('%s', this.triggerConfiguration.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.activateMessageTitle, activateMessageBody, '',
        this.activateMessageOK).subscribe(
        {
          next: userConfirmed => {
            if (userConfirmed) {
              this.loadingIndicator.show(this.translate.instant('Activating'));
              if (this.machine.id) {
                this.triggerConfigurationService.activateTriggerConfig(this.machine.id, this.triggerConfiguration.id)
                .subscribe(
                    {
                      next: (updatedTriggerConfiguration: TriggerConfiguration) => {
                        this.triggerConfiguration.isActive = true;
                        this.initialize();
                        const emitConfig = (updatedTriggerConfiguration && updatedTriggerConfiguration.id) ? updatedTriggerConfiguration : this.triggerConfiguration;
                        this.updatedTriggerConfigurationEvent.emit(emitConfig);
                        this.loadingIndicator.hide();
                        this.notification.success(this.translate.instant('trigger configuration activated, trigger:') + ' ' + this.triggerConfiguration.name, this.COMPONENT_NAME, false);
                      }, error: (error) => {
                        this.triggerConfiguration.isActive = false;
                        this.initialize();
                        this.loadingIndicator.hide();
                        if (error.status === this.GAP_DETECTION_IN_PROGRESS_STATUS_ERROR) {
                          this.notification.error(this.translate.instant('Could not activate trigger configuration. Gap detection in progress'), error, this.COMPONENT_NAME, true);
                        } else {
                          this.notification.error(this.translate.instant('Error activating trigger configuration'), error, this.COMPONENT_NAME, true);
                        }
                      }
                    });
              }
            }
          }, error: () => {
            this.loadingIndicator.hide();
          }
        });
  }

  onDeactivateTriggerConfig(): void {
    const deactivateMessageBody = this.deactivateMessageBody.replace('%s', this.triggerConfiguration.name.toBold());
    if (this.isGapDetectionInProgress[this.triggerConfiguration.id] === true) {
      this.deactivateMessageBody = this.translate.instant('Gap detection in progress. Gap detection process will be interrupted for current trigger.') +
          '</br>' + this.deactivateMessageBody;
    }

    this.confirmationDialogService.handleConfirmationDialog(this.deactivateMessageTitle, deactivateMessageBody, '',
        this.deactivateMessageOK).subscribe(
        {
          next: userConfirmed => {
            if (userConfirmed) {
              this.loadingIndicator.show(this.translate.instant('Deactivating'));
              if (this.machine.id) {
                this.triggerConfigurationService.deactivateTriggerConfig(this.machine.id, this.triggerConfiguration.id)
                .subscribe(
                    {
                      next: (updatedTriggerConfiguration: TriggerConfiguration) => {
                        this.triggerConfiguration.isActive = false;
                        this.initialize();
                        const emitConfig = (updatedTriggerConfiguration && updatedTriggerConfiguration.id) ? updatedTriggerConfiguration : this.triggerConfiguration;
                        this.updatedTriggerConfigurationEvent.emit(emitConfig);
                        this.loadingIndicator.hide();
                        this.notification.success(this.translate.instant('Trigger configuration deactivated, trigger:') + ' ' + this.triggerConfiguration.name, this.COMPONENT_NAME, false);
                      }, error: (error) => {
                        this.triggerConfiguration.isActive = true;
                        this.initialize();
                        this.loadingIndicator.hide();
                        if (error.status === this.GAP_DETECTION_IN_PROGRESS_STATUS_ERROR) {
                          this.notification.error(this.translate.instant('Could not deactivate trigger configuration. Gap detection in progress'), error, this.COMPONENT_NAME, true);
                        } else {
                          this.notification.error(this.translate.instant('Error deactivating trigger configuration'), error, this.COMPONENT_NAME, true);
                        }
                      }
                    });
              }
            }
          }, error: () => {
            this.loadingIndicator.hide();
          }
        });
  }

  onForceTriggerExecution(): void {
    const forceTriggerExecutionMessageBody = this.forceTriggerExecutionMessageBody.replace('%s', this.triggerConfiguration.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.forceTriggerExecutionMessageTitle, forceTriggerExecutionMessageBody, '',
        this.forceTriggerExecutionMessageOK).subscribe({
      next: userConfirmed => {
        if (userConfirmed) {
          this.loadingIndicator.show(this.translate.instant('Forcing trigger execution'));
          if (this.machine.id) {
            this.triggerConfigurationService.forceTriggerExecution(this.machine.id, this.triggerConfiguration.id).subscribe(
                {
                  next: (updatedTriggerConfiguration: TriggerConfiguration) => {
                    this.updatedTriggerConfigurationEvent.emit(updatedTriggerConfiguration);
                    this.loadingIndicator.hide();
                    this.notification.success(this.translate.instant('Trigger configuration is forced:') + ' ' + this.triggerConfiguration.name, this.COMPONENT_NAME, false);
                  },
                  error: (error) => {
                    this.loadingIndicator.hide();
                    if (error.status === this.GAP_DETECTION_IN_PROGRESS_STATUS_ERROR) {
                      console.error('TriggerConfig. Could not force the trigger execution.', error);
                    }
                    this.notification.error(this.translate.instant('Could not force the trigger execution.'), error, this.COMPONENT_NAME, true);
                  }
                });
          }
        }
      }, error: () => {
        this.loadingIndicator.hide();
      }
    });
  }

  getModal(): NgbModalRef {
    const modalRef = this.modalService.open(TriggerConfigurationEditComponent, {size: 'xl'});
    this.setModalInputParameters(modalRef);
    return modalRef;
  }

  private initialize(): void {
    this.setDeleteButtonIsDisabled();
    this.setActivateButtonIsDisabled();
    this.setDeactivateButtonIsDisabled();
    this.setForceTriggerIsDisabled();
    this.setShowActivateTriggerButton();
    this.setShowDeactivateTriggerButton();
  }

  private onUserConfirmedDeleteTriggerConfiguration(): void {
    if (!this.machine.id) {
      return;
    }
    this.loadingIndicator.show(this.translate.instant('Deleting'));
    this.triggerConfigurationService.delete(this.machine.id, this.triggerConfiguration.id).subscribe({
      next: () => {
        this.deleteEvent.emit(this.triggerConfiguration);
        this.loadingIndicator.hide();
        this.notification.success(this.translate.instant('Trigger configuration') + ' ' +
            this.triggerConfiguration.name + '(' + this.triggerConfiguration.id + ') ' + this.translate.instant('deleted'), this.COMPONENT_NAME, false);
      },
      error: (error) => {
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error deleting trigger configuration'), error, this.COMPONENT_NAME, true);
      }
    });
  }

  private setModalInputParameters(modalRef: NgbModalRef) {
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance._triggerConfiguration = this.triggerConfiguration;
  }
}
